import { Component, ElementRef, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef, OnInit, AfterViewInit, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ICountryMap } from "../../interfaces/icountrymap";
import { CountryMapService } from "../../services/countrymap.service";
import { AuthService } from "../../services/auth.service";
declare var google: any;

@Component({
    selector: "map",
    templateUrl: "./map.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MapComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild("map") map!: ElementRef;
    @ViewChild("mapContainer") mapContainer!: ElementRef;

    dataTable!: ICountryMap[];
    options!: Object;
    width: number = 400;
    region: string = "world";
    mapData!: any;
    colourValues!: number[];
    colourNames!: string[];
    chart: any;
    isMobileDevice: boolean = false;
    private destroy$ = new Subject<void>();
    private viewInitialized = false;

    constructor(
        private countryMapService: CountryMapService,
        private router: Router,
        private cdr: ChangeDetectorRef
    ) {
        this.isMobileDevice = window.navigator.userAgent.toLowerCase().indexOf("mobi") > -1;
        let com: any = this;
        this.countryMapService.dropDownsChangedAnnounced$
            .pipe(takeUntil(this.destroy$))
            .subscribe(countryValues => {
                this.dataTable = countryValues.countryMap;
                if (countryValues.regionCountryCode != null) {
                    this.region = countryValues.regionCountryCode;
                }
                if (this.viewInitialized) {
                    setTimeout(() => {
                        com.drawChart();
                        com.cdr.markForCheck();
                    }, 500);
                }
            });

        this.countryMapService.screenSizeChangedAnnounced$
            .pipe(takeUntil(this.destroy$))
            .subscribe(screenSize => {
                if (this.viewInitialized) {
                    setTimeout(() => {
                        com.drawChart();
                        com.cdr.markForCheck();
                    }, 500);
                }
            });
    }

    drawChart(): void {
        if (!this.viewInitialized || !this.map || !this.mapContainer) {
            return;
        }
        
        if (typeof google === 'undefined' || google == null || google.visualization == null) {
            let dc: any = this;
            setTimeout(() => dc.drawChart(), 500);
        } else {
            if (google.visualization.GeoChart == null) {
                google.charts.load("45.1", { "packages": ["geochart"] });
                google.charts.setOnLoadCallback(() => this.drawChart());
            } else {
                this.getDataForMap();
                let toolTipTrigger: string = this.isMobileDevice ? "selection" : "focus";
                this.options = {
                    legend: "none",
                    width: this.mapContainer.nativeElement.clientWidth,
                    region: this.region,
                    chartArea: { left: 0, top: 0, bottom: 0, width: this.mapContainer.nativeElement.clientWidth },
                    colorAxis: { colors: this.colourNames, values: this.colourValues },
                    displayMode: "regions",
                    tooltip: { isHtml: true, trigger: toolTipTrigger }
                };

                this.chart = new google.visualization.GeoChart(this.map.nativeElement);

                this.chart.draw(this.mapData, this.options);
                var mapCom: any = this;
                if (!this.isMobileDevice) {
                    google.visualization.events.addListener(this.chart, "select", function (): void {
                        mapCom.chartClicked(mapCom.chart);
                    });
                }
            }
        }
    }

    ngOnInit(): void {
        // Data will be loaded via subscription in constructor
    }

    ngAfterViewInit(): void {
        this.viewInitialized = true;
        // Trigger initial chart draw if we already have data
        if (this.dataTable && this.dataTable.length > 0) {
            setTimeout(() => {
                this.drawChart();
                this.cdr.markForCheck();
            }, 100);
        }
    }

    chartClicked(chart: any): void {
        var selection: any = chart.getSelection()[0];
        if (selection) {
            var country: any = this.mapData.getValue(selection.row, 0);
            this.router.navigate([`/${AuthService.businessFunction?.defaultRoute || ""}/countryinformation`, country]);
        }
    }

    getDataForMap(): void {

        let data: any = new google.visualization.DataTable();
        let colours: any[] = new Array();

        data.addColumn("string", "Locale");
        data.addColumn("number", "statusId");
        data.addColumn({ type: "string", role: "tooltip", p: { html: true } });

        if (this.dataTable) {
            this.dataTable.forEach((item: ICountryMap) => {
                let readMoreText: string = "";
                if (this.isMobileDevice) {
                    readMoreText = `<div class="map__view-more"> <a href="${AuthService.businessFunction?.defaultRoute || ""}/countryinformation/${item.countryCode}"> Read More</a></div>`;
                }

                // Custom tooltip styling for single line display
                const tooltipWidth = Math.min(300, this.mapContainer.nativeElement.clientWidth * 0.2);
                const tooltipStyles = `
                    width: auto; 
                    height: auto; 
                    min-width: 200px;
                    overflow: hidden; 
                    text-overflow: ellipsis; 
                    padding: 8px 12px; 
                    font-size: 14px; 
                    line-height: 1.2;
                `;

                data.addRows([[{ v: item.countryCode, f: item.countryName }, item.countryValue, `<div class="map__tooltip" style="${tooltipStyles}">${item.text}${readMoreText}</div>`]]);
                colours.push({ colour: item.colour, value: item.countryValue });
            });
            this.colourNames = new Array();
            this.colourValues = new Array();
            // order colours and values in ascending order
            colours.sort(function (a: any, b: any): any {
                return a.value - b.value;
            });
            colours.forEach((item) => {
                this.colourValues.push(item.value);
                this.colourNames.push(item.colour);
            });
        }

        this.mapData = data;
    }

    onResize(event: any): void {
        this.countryMapService.screenSizeChanged(this.mapContainer.nativeElement.clientWidth);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}